import {
  BookOpenIcon,
  PencilIcon,
  PlusIcon,
  TrashIcon,
} from '@heroicons/react/24/outline'
import type { Title } from '@reader-tracker/shared/lib/database.types'
import { supabase } from '@reader-tracker/shared/lib/supabase'
import { useEffect, useState } from 'react'
import { Link } from 'react-router-dom'

export default function MangaListPage() {
  const [titles, setTitles] = useState<Title[]>([])
  const [isLoading, setIsLoading] = useState(true)
  const [deletingId, setDeletingId] = useState<string | null>(null)

  useEffect(() => {
    async function load() {
      const { data } = await supabase
        .from('titles')
        .select('*')
        .order('canonical_name')
      setTitles(data ?? [])
      setIsLoading(false)
    }
    load()
  }, [])

  async function handleDelete(id: string, name: string) {
    if (
      !confirm(`Delete "${name}"? This also removes all chapters and follows.`)
    )
      return
    setDeletingId(id)
    await supabase.from('titles').delete().eq('id', id)
    setTitles((prev) => prev.filter((t) => t.id !== id))
    setDeletingId(null)
  }

  if (isLoading) {
    return (
      <div className="flex justify-center py-20">
        <div className="h-7 w-7 animate-spin rounded-full border-4 border-blue-600 border-t-transparent" />
      </div>
    )
  }

  return (
    <div>
      <div className="mb-6 flex items-center justify-between">
        <h1 className="font-bold text-gray-900 text-xl">Manga Titles</h1>
        <Link
          to="/admin/manga/new"
          className="inline-flex items-center gap-2 rounded-lg bg-blue-600 px-4 py-2 text-sm text-white hover:bg-blue-700"
        >
          <PlusIcon className="h-4 w-4" />
          Add title
        </Link>
      </div>

      {titles.length === 0 ? (
        <div className="flex flex-col items-center gap-3 py-20 text-gray-400">
          <BookOpenIcon className="h-12 w-12 text-gray-200" />
          <p>No titles yet. Add one above.</p>
        </div>
      ) : (
        <div className="overflow-hidden rounded-xl border border-gray-200 bg-white">
          <table className="min-w-full divide-y divide-gray-200 text-sm">
            <thead className="bg-gray-50">
              <tr>
                <th className="px-4 py-3 text-left font-medium text-gray-600">
                  Cover
                </th>
                <th className="px-4 py-3 text-left font-medium text-gray-600">
                  Title
                </th>
                <th className="px-4 py-3 text-left font-medium text-gray-600">
                  Type
                </th>
                <th className="px-4 py-3 text-left font-medium text-gray-600">
                  Status
                </th>
                <th className="px-4 py-3 text-right font-medium text-gray-600">
                  Actions
                </th>
              </tr>
            </thead>
            <tbody className="divide-y divide-gray-100">
              {titles.map((t) => (
                <tr key={t.id} className="hover:bg-gray-50">
                  <td className="px-4 py-3">
                    <div className="h-12 w-9 overflow-hidden rounded bg-gray-100">
                      {t.cover_url ? (
                        <img
                          src={t.cover_url}
                          alt={t.canonical_name}
                          className="h-full w-full object-cover"
                        />
                      ) : (
                        <div className="flex h-full items-center justify-center">
                          <BookOpenIcon className="h-5 w-5 text-gray-300" />
                        </div>
                      )}
                    </div>
                  </td>
                  <td className="px-4 py-3 font-medium text-gray-900">
                    {t.canonical_name}
                  </td>
                  <td className="px-4 py-3 text-gray-600 capitalize">
                    {t.type}
                  </td>
                  <td className="px-4 py-3 text-gray-600 capitalize">
                    {t.status}
                  </td>
                  <td className="px-4 py-3">
                    <div className="flex items-center justify-end gap-2">
                      <Link
                        to={`/admin/manga/${t.id}/sources`}
                        className="rounded px-2 py-1 text-gray-500 text-xs hover:bg-gray-100"
                        title="Sources"
                      >
                        Sources
                      </Link>
                      <Link
                        to={`/admin/manga/${t.id}/chapters`}
                        className="rounded px-2 py-1 text-gray-500 text-xs hover:bg-gray-100"
                        title="Chapters"
                      >
                        Chapters
                      </Link>
                      <Link
                        to={`/admin/manga/${t.id}/edit`}
                        className="rounded p-1 text-gray-500 hover:bg-gray-100 hover:text-blue-600"
                        title="Edit"
                      >
                        <PencilIcon className="h-4 w-4" />
                      </Link>
                      <button
                        type="button"
                        onClick={() => handleDelete(t.id, t.canonical_name)}
                        disabled={deletingId === t.id}
                        className="rounded p-1 text-gray-500 hover:bg-red-50 hover:text-red-600"
                        title="Delete"
                      >
                        <TrashIcon className="h-4 w-4" />
                      </button>
                    </div>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}
    </div>
  )
}
